import { createContext, createSignal, type JSX, useContext, createEffect, on } from 'solid-js'
import { DataType } from '../../features/revenue/domain/models/mrr'
import { type Grouping, getDefaultGrouping } from '../../features/revenue/domain/models/range'
import type { SimplePeriodType, CustomDateRange } from '../helpers/date.functions'
import { useUser } from './user-provider'

const DEFAULT_PERIOD: SimplePeriodType = 'last_30_days'

export interface MRRSelectionContextType {
  selectedPeriod: () => SimplePeriodType
  setSelectedPeriod: (period: SimplePeriodType) => void
  customDateRange: () => CustomDateRange | undefined
  setCustomDateRange: (range: CustomDateRange) => void
  selectedGrouping: () => Grouping
  setSelectedGrouping: (grouping: Grouping) => void
  selectedDataType: () => DataType
  setSelectedDataType: (dataType: DataType) => void
}

const [selectedPeriod, setSelectedPeriod] = createSignal<SimplePeriodType>(DEFAULT_PERIOD)
const [customDateRange, setCustomDateRange] = createSignal<CustomDateRange | undefined>(undefined)
const [selectedGrouping, setSelectedGrouping] = createSignal<Grouping>(getDefaultGrouping(DEFAULT_PERIOD, undefined))
const [selectedDataType, setSelectedDataType] = createSignal<DataType>(DataType.Users)

const store: MRRSelectionContextType = {
  selectedPeriod,
  setSelectedPeriod: (period: SimplePeriodType) => {
    setSelectedPeriod(period)
    const custom = period === 'custom' ? customDateRange() : undefined
    setSelectedGrouping(getDefaultGrouping(period, custom))
  },
  customDateRange,
  setCustomDateRange: (range: CustomDateRange) => {
    setCustomDateRange(range)
    setSelectedGrouping(getDefaultGrouping('custom', range))
  },
  selectedGrouping,
  setSelectedGrouping,
  selectedDataType,
  setSelectedDataType
}

const MRRSelectionContext = createContext<MRRSelectionContextType>(store)

export const MRRSelectionProvider = (props: { children: JSX.Element }): JSX.Element => {
  const { organizationSlug } = useUser()

  createEffect(
    on(
      () => organizationSlug(),
      () => {
        setSelectedPeriod(DEFAULT_PERIOD)
        setCustomDateRange(undefined)
        setSelectedGrouping(getDefaultGrouping(DEFAULT_PERIOD, undefined))
        setSelectedDataType(DataType.Users)
      }
    )
  )

  return <MRRSelectionContext.Provider value={store}>{props.children}</MRRSelectionContext.Provider>
}

export const useMRRSelection = (): MRRSelectionContextType => {
  return useContext(MRRSelectionContext)
}
